import type {
  AttachmentAdapter,
  CompleteAttachment,
  PendingAttachment,
  Unstable_DirectiveFormatter,
} from '@assistant-ui/react';
import {
  SPEC_FILE_ACCEPT,
  SPEC_FILE_KINDS,
  SPEC_FILE_LIMITS,
  type SpecFileKind,
  type SpecFilePart,
  type SpecFileSource,
  type SpecFileStatus,
  parseSpecFileDirectives,
  readSpecFilePart,
  sanitizeSpecFileName,
  specFileDirective,
} from '@parcel/types';
import { ApiError } from './api.js';

/** A file as the API returns it. */
export interface SpecFileRecord {
  id: string;
  conversationId: string | null;
  messageId: string | null;
  source: SpecFileSource;
  name: string;
  mimeType: string;
  size: number;
  kind: SpecFileKind;
  status: SpecFileStatus;
  pages: number | null;
  truncated: boolean;
  error: string | null;
  createdAt: string;
}

export function readSpecFileRecord(value: unknown): SpecFileRecord | null {
  const record = (value ?? null) as Record<string, unknown> | null;
  if (!record || typeof record !== 'object') return null;
  const { id, name, mimeType, size, kind, status } = record;
  if (typeof id !== 'string' || typeof name !== 'string' || typeof mimeType !== 'string') {
    return null;
  }
  if (typeof size !== 'number' || !(SPEC_FILE_KINDS as readonly unknown[]).includes(kind)) {
    return null;
  }
  if (status !== 'pending' && status !== 'ready' && status !== 'failed') return null;
  return {
    id,
    conversationId: typeof record.conversationId === 'string' ? record.conversationId : null,
    messageId: typeof record.messageId === 'string' ? record.messageId : null,
    source: record.source === 'generated' ? 'generated' : 'upload',
    name,
    mimeType,
    size,
    kind: kind as SpecFileKind,
    status,
    pages: typeof record.pages === 'number' ? record.pages : null,
    truncated: record.truncated === true,
    error: typeof record.error === 'string' ? record.error : null,
    createdAt: typeof record.createdAt === 'string' ? record.createdAt : '',
  };
}

// ---------------------------------------------------------------------------
// Images are shrunk before upload: the model pays per pixel
// ---------------------------------------------------------------------------

const SHRINKABLE = new Set(['image/png', 'image/jpeg', 'image/webp']);

export function downscaleTarget(
  width: number,
  height: number,
  maxEdge: number = SPEC_FILE_LIMITS.imageMaxEdge,
): { width: number; height: number } | null {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return null;
  const scale = maxEdge / longest;
  // At least one pixel: a 4000x1 sliver would otherwise round to a zero-height canvas.
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export type ImageMeasure = (file: File) => Promise<{ width: number; height: number }>;
export type ImageEncoder = (
  file: File,
  width: number,
  height: number,
  type: string,
) => Promise<Blob>;

const defaultMeasure: ImageMeasure = async (file) => {
  const bitmap = await createImageBitmap(file);
  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return size;
};

const defaultEncode: ImageEncoder = async (file, width, height, type) => {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('No canvas context.');
  if (type === 'image/jpeg') {
    // JPEG has no alpha; a transparent screenshot would otherwise turn black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Encode failed.'))),
      type,
      0.85,
    ),
  );
};

/** At most 1,568 px on the long side and 5 MB; anything that is not a PNG, JPEG or WebP passes through. */
export async function prepareSpecImage(
  file: File,
  deps: { measure?: ImageMeasure; encode?: ImageEncoder } = {},
): Promise<File> {
  if (!SHRINKABLE.has(file.type)) return file;
  const measure = deps.measure ?? defaultMeasure;
  const encode = deps.encode ?? defaultEncode;
  let size: { width: number; height: number };
  try {
    size = await measure(file);
  } catch {
    return file;
  }
  const target =
    downscaleTarget(size.width, size.height) ??
    (file.size > SPEC_FILE_LIMITS.imageMaxBytes ? size : null);
  if (!target) return file;
  let blob = await encode(file, target.width, target.height, file.type);
  if (blob.size > SPEC_FILE_LIMITS.imageMaxBytes && file.type !== 'image/jpeg') {
    blob = await encode(file, target.width, target.height, 'image/jpeg');
  }
  const name =
    blob.type === file.type ? file.name : `${file.name.replace(/\.[a-z0-9]+$/i, '')}.jpg`;
  return new File([blob], name, { type: blob.type || file.type });
}

// ---------------------------------------------------------------------------
// The attachment adapter: upload, wait for the text, hand the part to send
// ---------------------------------------------------------------------------

export interface SpecFileClient {
  uploadFile(workspaceId: string, file: File): Promise<SpecFileRecord>;
  getFile(workspaceId: string, fileId: string): Promise<SpecFileRecord>;
  deleteFile(workspaceId: string, fileId: string): Promise<void>;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const UPLOAD_FAILED = 'Couldn’t upload this file. Try again in a moment.';

/** Member-facing copy for a refused upload: the API's own file messages, never runtime internals. */
function failure(error: unknown): string {
  if (!(error instanceof ApiError)) return UPLOAD_FAILED;
  const body = (error.body ?? null) as { error?: { code?: unknown; message?: unknown } } | null;
  const { code, message } = body?.error ?? {};
  if (code === 'invalid_file' && typeof message === 'string' && message.length > 0) return message;
  if (code === 'spec_read_only' || code === 'spec_runs_disabled') {
    return 'Spec is paused, so files can’t be added right now.';
  }
  if (code === 'spec_seat_required') {
    return 'You need an AI seat to use Spec in this workspace. Ask an admin to assign you one.';
  }
  if (code === 'spec_funds_exhausted') {
    return 'This workspace has no credits available for Spec right now.';
  }
  if (code === 'spec_spend_limit_reached') {
    return 'This workspace has reached its monthly spend limit. An owner or admin can raise it.';
  }
  if (code === 'spec_not_launched') return 'Spec isn’t available right now.';
  if (error.status === 401 || error.status === 403) {
    return 'You don’t have access to add files in this workspace.';
  }
  return UPLOAD_FAILED;
}

/** The file part a sent attachment carries; null for one this adapter did not make. */
export function specFilePartOf(attachment: CompleteAttachment): SpecFilePart | null {
  const part = attachment.content.find((item) => item.type === 'file');
  if (!part || part.type !== 'file') return null;
  const parcel = (part.providerMetadata as { parcel?: Record<string, unknown> } | undefined)
    ?.parcel;
  return readSpecFilePart({
    type: 'file',
    fileId: part.data,
    name: part.filename,
    mimeType: part.mimeType,
    size: parcel?.size,
    kind: parcel?.kind,
    ...(parcel?.pages == null ? {} : { pages: parcel.pages }),
  });
}

export function createSpecFileAttachmentAdapter(options: {
  client: SpecFileClient;
  workspaceId: string;
  pollMs?: number;
  timeoutMs?: number;
  prepare?: (file: File) => Promise<File>;
}): AttachmentAdapter {
  const { client, workspaceId } = options;
  const pollMs = options.pollMs ?? 1_000;
  const timeoutMs = options.timeoutMs ?? 120_000;
  const prepare = options.prepare ?? ((file: File) => prepareSpecImage(file));
  // Client attachment id to the server record, so send and remove know what was uploaded.
  const uploaded = new Map<string, SpecFileRecord>();

  return {
    accept: SPEC_FILE_ACCEPT,
    async *add({ file }) {
      const base = {
        id: crypto.randomUUID(),
        type: file.type.startsWith('image/') ? 'image' : 'document',
        name: sanitizeSpecFileName(file.name),
        contentType: file.type,
        file,
      } satisfies Omit<PendingAttachment, 'status'>;
      yield { ...base, status: { type: 'running', reason: 'uploading', progress: 0 } };
      let record: SpecFileRecord;
      try {
        record = await client.uploadFile(workspaceId, await prepare(file));
        uploaded.set(base.id, record);
        const deadline = Date.now() + timeoutMs;
        while (record.status === 'pending' && Date.now() < deadline) {
          await sleep(pollMs);
          record = await client.getFile(workspaceId, record.id);
          uploaded.set(base.id, record);
        }
      } catch (error) {
        yield { ...base, status: { type: 'incomplete', reason: 'error', message: failure(error) } };
        return;
      }
      if (record.status !== 'ready') {
        const message =
          record.status === 'failed'
            ? (record.error ?? 'Could not read this file.')
            : 'This file is taking too long to read.';
        yield { ...base, status: { type: 'incomplete', reason: 'error', message } };
        return;
      }
      yield { ...base, status: { type: 'requires-action', reason: 'composer-send' } };
    },
    async send(attachment) {
      const record = uploaded.get(attachment.id);
      if (!record || record.status !== 'ready') throw new Error('This file is not ready.');
      return {
        ...attachment,
        status: { type: 'complete' },
        content: [
          {
            type: 'file',
            data: record.id,
            mimeType: record.mimeType,
            filename: record.name,
            sourceType: 'id',
            providerMetadata: {
              parcel: { kind: record.kind, size: record.size, pages: record.pages },
            },
          },
        ],
      };
    },
    async remove(attachment) {
      const record = uploaded.get(attachment.id);
      uploaded.delete(attachment.id);
      // Best effort: the API refuses a sent file, and an upload left behind is swept after a day.
      if (record) {
        await client.deleteFile(workspaceId, record.id).catch(() => undefined);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Directives: a file sits in the text as :file[name]{id=uuid}
// ---------------------------------------------------------------------------

export const specFileDirectiveFormatter: Unstable_DirectiveFormatter = {
  serialize(item) {
    return specFileDirective(item.label, item.id);
  },
  parse(text) {
    return parseSpecFileDirectives(text).map((segment) =>
      segment.kind === 'text'
        ? { kind: 'text' as const, text: segment.text }
        : { kind: 'mention' as const, type: 'file', label: segment.name, id: segment.id },
    );
  },
};

type OutgoingPart = { type: 'text'; text: string } | SpecFilePart;

/**
 * The parts a send carries: the text with each chip's client id swapped for the file's id, then
 * the files in chip order. A file without a chip gets one at the end; a chip without a file
 * becomes its plain name.
 */
export function outgoingSpecParts(
  text: string,
  attachments: readonly CompleteAttachment[],
): OutgoingPart[] {
  const byClientId = new Map<string, SpecFilePart>();
  for (const attachment of attachments) {
    const part = specFilePartOf(attachment);
    if (part) byClientId.set(attachment.id, part);
  }
  const files: SpecFilePart[] = [];
  const placed = new Set<string>();
  const pieces = parseSpecFileDirectives(text).map((segment) => {
    if (segment.kind === 'text') return segment.text;
    const part = byClientId.get(segment.id);
    if (!part) return `[${segment.name}]`;
    if (!placed.has(segment.id)) {
      placed.add(segment.id);
      files.push(part);
    }
    return specFileDirective(part.name, part.fileId);
  });
  let body = pieces.join('');
  for (const [clientId, part] of byClientId) {
    if (placed.has(clientId)) continue;
    files.push(part);
    body = `${body}${body.length > 0 ? ' ' : ''}${specFileDirective(part.name, part.fileId)}`;
  }
  return [{ type: 'text', text: body }, ...files];
}

/**
 * Opens a sent file: images and PDFs in a new tab (opened before the fetch, so the click still
 * counts as the gesture), everything else as a download. False when the file could not be read.
 */
export async function openSpecFile(
  load: () => Promise<Blob>,
  file: { name: string; kind: string },
): Promise<boolean> {
  const viewable = file.kind === 'image' || file.kind === 'pdf';
  const tab = viewable ? window.open('', '_blank') : null;
  try {
    const url = URL.createObjectURL(await load());
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  } catch {
    tab?.close();
    return false;
  }
}
